import { LiveCaveat, LiveCartTotals, LiveMetricKey, LiveOfficialMetrics } from '../types'

/**
 * Métricas OFICIAIS de live + ressalvas, do jeito que vão para o cliente.
 *
 * Os nomes seguem `live-metrics.ts`: **atribuída** (padrão, catálogo da live),
 * **atribuída direta** (carrinho Widde na live) e **influenciada** (fora do
 * catálogo). O "carrinho da live" é secundário e é o que mais depende do GA4
 * da loja — por isso as ressalvas aparecem logo abaixo, e cada KPI afetado
 * leva a marca "ressalva" em vez de parecer tão firme quanto os outros.
 */

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(value || 0)
}

const formatInt = (n: number) => (n || 0).toLocaleString('pt-BR')

/** Soma o carrinho da live de várias lives (cada carrinho é de uma live). */
export function sumLiveCarts(carts: Array<LiveCartTotals | undefined>): LiveCartTotals | null {
  const present = carts.filter((c): c is LiveCartTotals => !!c)
  if (present.length === 0) return null
  const sum = (pick: (c: LiveCartTotals) => number) => present.reduce((s, c) => s + (pick(c) || 0), 0)
  const vb = (pick: (c: LiveCartTotals) => { items: number; value: number }) => ({
    items: sum((c) => pick(c).items),
    value: sum((c) => pick(c).value),
  })
  return {
    items: sum((c) => c.items),
    value: sum((c) => c.value),
    viewers: sum((c) => c.viewers),
    valueIsPartial: present.some((c) => c.valueIsPartial),
    byOrigin: { widde: vb((c) => c.byOrigin.widde), ga4: vb((c) => c.byOrigin.ga4), both: vb((c) => c.byOrigin.both) },
    purchased: vb((c) => c.purchased),
    abandoned: vb((c) => c.abandoned),
    pending: vb((c) => c.pending),
    inferredItems: sum((c) => c.inferredItems),
    unconfirmed: vb((c) => c.unconfirmed),
  }
}

const affects = (caveats: LiveCaveat[], key: LiveMetricKey) => caveats.some((c) => c.affects.includes(key))

export function LiveMetricsSummary({ metrics, legacy, cart, caveats, scope }: {
  /** Ausente em backend antigo: só a atribuída (pelo campo legado) é mostrada. */
  metrics?: LiveOfficialMetrics
  legacy: { orders: number; revenue: number }
  cart: LiveCartTotals | null
  caveats: LiveCaveat[] | undefined
  /** "no período" ou "nesta live" — vai no rótulo. */
  scope: string
}) {
  const list = caveats ?? []
  const attributed = metrics?.attributed ?? legacy
  const direct = metrics ? metrics.attributedDirect : undefined
  const influenced = metrics?.influenced

  return (
    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ink-100, #e5e7eb)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
        <MetricTile
          label="Receita atribuída"
          value={formatCurrency(attributed.revenue)}
          sub={`${formatInt(attributed.orders)} pedidos · espectadores compraram produto do catálogo da live`}
          emphasis
        />
        <MetricTile
          label="Receita atribuída direta"
          value={direct ? formatCurrency(direct.revenue) : 'Não apurada'}
          sub={
            direct
              ? `${formatInt(direct.orders)} pedidos · adicionaram ao carrinho da Widde pela live`
              : direct === null
                ? 'falha ao ler o carrinho da Widde nesta execução'
                : 'disponível depois de reprocessar com a versão nova'
          }
          caveat={affects(list, 'attributedDirect')}
          muted={!direct}
        />
        <MetricTile
          label="Receita influenciada"
          value={influenced ? formatCurrency(influenced.revenue) : 'Não apurada'}
          sub={
            influenced
              ? `${formatInt(influenced.orders)} pedidos · compras dos espectadores fora do catálogo`
              : 'disponível depois de reprocessar com a versão nova'
          }
          muted={!influenced}
        />
        <MetricTile
          label={`Carrinho da live`}
          value={cart ? formatCurrency(cart.value) : 'Não apurado'}
          sub={
            cart
              ? `${formatInt(cart.items)} itens ${scope} · comprado ${formatCurrency(cart.purchased.value)} · abandonado ${formatCurrency(cart.abandoned.value)}` +
                (cart.pending.items > 0 ? ` · pendente ${formatCurrency(cart.pending.value)}` : '')
              : 'sem leitura do carrinho nesta execução'
          }
          detail={
            cart
              ? `Widde ${formatCurrency(cart.byOrigin.widde.value)} · loja (GA4) ${formatCurrency(cart.byOrigin.ga4.value)} · nas duas ${formatCurrency(cart.byOrigin.both.value)}` +
                (cart.unconfirmed.items > 0 ? ` · ${formatInt(cart.unconfirmed.items)} itens com horário não confirmado fora do total` : '') +
                (cart.valueIsPartial ? ' · há item sem preço (valor é piso)' : '')
              : undefined
          }
          caveat={affects(list, 'liveCart')}
          muted={!cart}
        />
      </div>
      <LiveCaveatsList caveats={list} />
    </div>
  )
}

function MetricTile({ label, value, sub, detail, caveat, emphasis, muted }: {
  label: string
  value: string
  sub: string
  detail?: string
  caveat?: boolean
  emphasis?: boolean
  muted?: boolean
}) {
  return (
    <div style={{
      border: `1px solid ${caveat ? '#fde68a' : 'var(--ink-100, #e5e7eb)'}`,
      background: caveat ? '#fffbeb' : emphasis ? '#fff7f3' : 'var(--paper, #fff)',
      borderRadius: 8,
      padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-500, #6b7280)' }}>
          {label}
        </span>
        {caveat && (
          <span title="Veja as ressalvas abaixo" style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '1px 7px' }}>
            ⚠ ressalva
          </span>
        )}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4, color: muted ? 'var(--ink-400, #9ca3af)' : emphasis ? 'var(--accent-coral, #ff7a59)' : 'var(--ink-800, #111827)' }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>
      {detail && <div style={{ fontSize: 10, color: 'var(--ink-400, #9ca3af)', marginTop: 4, lineHeight: 1.4 }}>{detail}</div>}
    </div>
  )
}

const SEVERITY_STYLE: Record<LiveCaveat['severity'], { bg: string; border: string; color: string; label: string }> = {
  // Âmbar, não vermelho: o número continua útil dentro do recorte que a
  // ressalva descreve — vermelho fazia o time descartar dado correto.
  critical: { bg: '#fff7ed', border: '#fdba74', color: '#9a3412', label: 'Dado parcial' },
  warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e', label: 'Atenção' },
  info: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: 'Aproximado' },
}

const METRIC_LABEL: Record<LiveMetricKey, string> = {
  attributed: 'receita atribuída',
  attributedDirect: 'receita atribuída direta',
  influenced: 'receita influenciada',
  liveCart: 'carrinho da live',
  abandonedCart: 'carrinho abandonado',
}

/** As ressalvas da execução, com o que cada uma afeta. Nada a dizer = não renderiza. */
export function LiveCaveatsList({ caveats }: { caveats: LiveCaveat[] }) {
  if (caveats.length === 0) return null
  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-500, #6b7280)' }}>
        Ressalvas desta análise
      </div>
      {caveats.map((c) => {
        const st = SEVERITY_STYLE[c.severity] ?? SEVERITY_STYLE.info
        return (
          <div key={c.code} style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--ink-700, #374151)', lineHeight: 1.5 }}>
            <strong style={{ color: st.color }}>{st.label}</strong>
            {c.affects.length > 0 && (
              <span style={{ color: st.color }}> · afeta {c.affects.map((a) => METRIC_LABEL[a] ?? a).join(', ')}</span>
            )}
            {c.liveKeys && c.liveKeys.length > 1 && <span style={{ color: 'var(--ink-500, #6b7280)' }}> · {c.liveKeys.length} lives</span>}
            <div>{c.message}</div>
          </div>
        )
      })}
    </div>
  )
}
